import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthService } from '@/auth/services';
import { DataTableComponent } from '@/components';
import {
    DataTableColumnType,
    FilterType
} from '@/enums';
import {
    DataTableColumn,
    ServiceMapping
} from '@/interfaces';
import { ServiceMappingsService } from '@/service';


@Component({
    selector: 'app-service-mapping-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    imports: [DataTableComponent],
})
export class ServiceMappingListingComponent {

    reloadTable$ = new Subject<void>();
    loading = false;
    columns: DataTableColumn<ServiceMapping>[] = [
        {
            field: 'is_mapped',
            header: 'Mapped',
            filterType: FilterType.BOOLEAN,
            filterInToolbar: true,
            tableVisible: false,
            sortable: false,
        },
        {
            field: 'service',
            header: 'Service (Raw)',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.serviceAutocomplete('service', q, context),
        },
        {
            field: 'count_of_occurrences',
            header: '# of Occurrences',
            filterType: FilterType.NUMERIC,
        },
        {
            field: 'is_excluded',
            header: 'Exclude',
            type: DataTableColumnType.CHECKBOX,
            filterType: FilterType.BOOLEAN,
            filterInToolbar: true,
            onCheckboxChange: (value: boolean, row: ServiceMapping) => this.edit(row, { is_excluded: value }),
        },
        {
            field: 'service_mapped',
            header: 'Service (Mapped)',
            filterType: FilterType.AUTOCOMPLETE,
            autocompleteCompleteMethod: (q, context) => this.serviceAutocomplete('service_mapped', q, context),
            editable: true,
            editType: FilterType.TEXT,
            onEditSave: (value, row) => {
                const mapped =
                    typeof value === 'string' ? value.trimEnd() : String(value ?? '').trimEnd();
                this.edit(row, { service_mapped: mapped });
            }
        },
        {
            field: 'updated_at',
            header: 'Updated Date/Time',
            type: DataTableColumnType.DATETIME,
            filterType: FilterType.DATE,
        },
        {
            field: 'updated_by_user.name',
            header: 'Last Updated By',
        },
        {
            field: 'actions',
            header: '',
            type: DataTableColumnType.ACTIONS,
            sortable: false,
            filterable: false,
            actions: [
                {
                    icon: 'pi pi-eye',
                    label: 'View Rates',
                    command: (row) =>
                        this.router.navigate(['/rates'], {
                            queryParams: {
                                service_name_mapped: row.service_mapped?.trim() ? row.service_mapped : row.service
                            }
                        }),
                    showIf: (row) => this.authService.hasPermission('service_mappings.service_mappings.read') && row.count_of_occurrences
                },
            ]
        }
    ];

    constructor(
        public authService: AuthService,
        private router: Router,
        private service: ServiceMappingsService,
        private messageService: MessageService,
    ) {
    }

    loadData = (params: Record<string, any>) => this.service.listing(params);

    exportData = (params: Record<string, any>) => this.service.export(params);

    serviceAutocomplete(field: 'service' | 'service_mapped', query: string, context: Record<string, any> = {}) {
        return this.service.autocomplete(field, query, context).pipe(map((res) => res.data ?? res));
    }

    edit(row: ServiceMapping, data: Record<string, any>): void {
        this.loading = true;
        this.service.edit(row.id, data).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'success',
                    detail: 'Service Mapping has been updated successfully.'
                });
                this.reloadTable$.next();
            },
            error: (err) => {
                this.loading = false;
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }
}
